import {Link, Pill} from '@wandb/components';
import React from 'react';

import {Skeleton} from '../../components/Skeleton';
import {DateFormat, format, formatDurationMilliseconds} from '../../util/date';
import * as urls from '../../util/urls';
import {versionGte} from '../../util/version';
import {useLatestWandbSdkVersion} from '../../util/wandbSdkVersion';

interface SdkVersionTagProps {
  currentVersion: string;
}

export const SdkVersionTag = ({currentVersion}: SdkVersionTagProps) => {
  const {data, loading, error} = useLatestWandbSdkVersion();

  if (error != null) {
    return null;
  }

  if (data == null) {
    return loading ? (
      <Skeleton className="rounded-full" height={22} width={110} />
    ) : null;
  }

  const {latestVersion, releasesByVersion} = data;
  if (!latestVersion || !currentVersion) {
    return null;
  }

  const newerAvailable =
    versionGte(latestVersion, currentVersion) &&
    latestVersion !== currentVersion;
  if (!newerAvailable) {
    return null;
  }

  const latestReleasedAt = releasesByVersion[latestVersion];
  const currentReleasedAt = releasesByVersion[currentVersion];

  let tooltipText: string | undefined;

  if (latestReleasedAt) {
    const latestDate = new Date(latestReleasedAt);
    const dateStr = format(latestDate, DateFormat.MONTH_DAY_YEAR);
    if (currentReleasedAt) {
      const behindMs =
        latestDate.getTime() - new Date(currentReleasedAt).getTime();
      tooltipText = `Released on ${dateStr} (${formatDurationMilliseconds(behindMs)} behind)`;
    } else {
      // unable to get public release date for current version (ex. dev version)
      tooltipText = `Released on ${dateStr}`;
    }
  }

  return (
    <Link
      href={urls.wandbSdkChangelog()}
      target="_blank"
      rel="noopener noreferrer">
      <Pill
        textValue={`${latestVersion} available`}
        color="gold"
        tooltip={tooltipText ? {children: tooltipText} : undefined}
      />
    </Link>
  );
};
